import { CustomAppEvents } from 'core/common/entities';
import { StorageInterface } from 'core/common/services';
import { checkIsClientSide } from 'core/common/utils/checkIsClientSide';
import { CookieConsentService } from 'core/cookie-consent/interfaces';
import { AnalyticsPixelId, SnapchatEventNames, SnapchatMethods } from '../../entities';
import { SnapchatService } from '../../interfaces/SnapchatService';

type DeferredEvent = {
  eventName: SnapchatEventNames;
  params?: Record<string, unknown>;
};

const noop: SnapchatMethods & { isAvailable: false } = {
  isAvailable: false as const,
  init: () => {},
  track: () => {},
};

export class HttpSnapchatService implements SnapchatService {
  private readonly maxDeferredEvents = 20;

  private readonly passedPixelIds: Array<AnalyticsPixelId> = [];
  private initializedPixelIds: Array<AnalyticsPixelId> = [];
  private deferredEvents: Array<DeferredEvent> = [];

  constructor(
    private readonly cookieConsentService: CookieConsentService,
    private readonly storageService?: StorageInterface,
    pixelIds?: Array<AnalyticsPixelId>,
  ) {
    this.passedPixelIds = !!pixelIds?.length ? pixelIds : [];
    this.cookieConsentService.addConsentLoadListener(this.onConsentLoad(this.passedPixelIds));
    this.cookieConsentService.addConsentChangeListener(this.onConsentChange(this.passedPixelIds));
  }

  isSnaptrRequired(): boolean {
    return !!this.passedPixelIds.length;
  }

  isSnaptrAvailable(): boolean {
    return this.snaptr.isAvailable;
  }

  track(eventName: SnapchatEventNames, params?: Record<string, unknown>) {
    if (this.isSnaptrRequired() && !this.initializedPixelIds.length) {
      this.deferEvent(eventName, params);
      return;
    }

    this.snaptr.track(eventName, params);
  }

  updateUserData(userData: { user_email: string }) {
    if (!this.snaptr.isAvailable || !this.initializedPixelIds.length) {
      return;
    }

    this.initializedPixelIds.forEach((pixelId) => {
      this.snaptr.init(pixelId, userData);
    });
  }

  private get snaptr(): SnapchatMethods & { isAvailable: boolean } {
    if (!checkIsClientSide() || !window.snaptr) {
      return noop;
    }

    const snaptr = window.snaptr;

    return {
      isAvailable: true as const,
      init: (pixelId: string, userData?: { user_email: string }) =>
        snaptr('init', pixelId, userData),
      track: (eventName: SnapchatEventNames, params?: Record<string, unknown>) =>
        snaptr('track', eventName, params),
    };
  }

  private onConsentLoad =
    (pixels: Array<AnalyticsPixelId>) => (consentState: { TARGETING: boolean }) => {
      if (consentState.TARGETING) {
        this.initializePixels(pixels);
      }
    };

  private onConsentChange =
    (pixels: Array<AnalyticsPixelId>) => (consentState: { TARGETING: boolean }) => {
      if (consentState.TARGETING) {
        this.initializePixels(pixels);
      }
    };

  private initializePixels(pixels: Array<AnalyticsPixelId>) {
    if (this.snaptr.isAvailable) {
      this.doInitializePixels(pixels);
      return;
    }

    document.addEventListener(
      CustomAppEvents.SNAPCHAT_PIXEL_LOADED,
      () => {
        this.doInitializePixels(pixels);
      },
      { once: true },
    );
  }

  private doInitializePixels(pixelsToInitialize: Array<AnalyticsPixelId>) {
    const userData = this.getUserDataFromStorage();

    pixelsToInitialize.forEach((pixel) => {
      if (this.initializedPixelIds.includes(pixel)) {
        return;
      }
      this.snaptr.init(pixel, userData);
      this.initializedPixelIds.push(pixel);
    });

    this.flushDeferredEvents();
  }

  private deferEvent(eventName: SnapchatEventNames, params?: Record<string, unknown>) {
    if (this.deferredEvents.length >= this.maxDeferredEvents) {
      return;
    }

    this.deferredEvents.push({ eventName, params });
  }

  private flushDeferredEvents() {
    if (!this.initializedPixelIds.length || !this.snaptr.isAvailable) {
      return;
    }

    const events = this.deferredEvents;
    this.deferredEvents = [];

    events.forEach(({ eventName, params }) => {
      this.snaptr.track(eventName, params);
    });
  }

  private getUserDataFromStorage(): { user_email: string } | undefined {
    try {
      if (!this.storageService) {
        return;
      }

      const storage = this.storageService.get<{ userProfile?: { email: string } }>('nebula');
      const email = storage?.userProfile?.email;

      return email ? { user_email: email } : undefined;
    } catch {
      return;
    }
  }
}
